<script setup>
import { computed, inject, ref, watch } from 'vue';
import { calendarDaysFromToday } from '@/lib/calendarDate';
import { donateUrl } from '../useSiteForm.js';
import CheckoutNotice from '../CheckoutNotice.vue';
import { campaignProgress } from '../donationProgress.js';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const editing = inject('tiqEditorCanvas', false);

const MAX_GIFT = 100000;

const donations = computed(() => props.site_data?.site?.donations || {});
const currency = computed(() => String(donations.value.currency || 'USD').toUpperCase());

// Whole dollars print without cents; anything else gets two places.
function money(n) {
    const v = Number(n) || 0;
    const whole = Number.isInteger(v);
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.value,
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: whole ? 0 : 2,
        }).format(v);
    } catch (e) {
        // An unknown currency code from an old settings row: print the
        // number rather than nothing, and say why in the console.
        console.warn('[donate] could not format currency', currency.value, e);
        return `${whole ? v.toLocaleString('en-US') : v.toFixed(2)} ${currency.value}`;
    }
}

// ── Campaign progress ───────────────────────────────────────────────
const goal = computed(() => Number(props.config.goal) || 0);
const progress = computed(() => campaignProgress(props.config, donations.value));
const raised = computed(() => progress.value.raised);
const donors = computed(() => progress.value.donors);
const pct = computed(() => (goal.value > 0 ? Math.min(100, (raised.value / goal.value) * 100) : 0));
const daysLeft = computed(() => {
    if (!props.config.ends_on) return null;
    const d = calendarDaysFromToday(props.config.ends_on);
    return d === null || d < 0 ? null : d;
});
const progressMeta = computed(() => {
    const parts = [];
    if (donors.value > 0) parts.push(`${donors.value.toLocaleString('en-US')} ${donors.value === 1 ? 'donor' : 'donors'}`);
    if (daysLeft.value !== null) {
        parts.push(daysLeft.value === 0 ? 'Last day' : `${daysLeft.value} ${daysLeft.value === 1 ? 'day' : 'days'} left`);
    }
    return parts.join(' · ');
});

// ── Payment methods ─────────────────────────────────────────────────
const methods = computed(() => (Array.isArray(donations.value.methods) ? donations.value.methods : []).filter((m) => m && m.key));
const monthlyMethods = computed(() => methods.value.filter((m) => m.monthly));
// Monthly is offered only when the block allows it AND something can
// actually take a recurring gift.
const canMonthly = computed(() => props.config.allow_monthly !== false && monthlyMethods.value.length > 0);

const frequency = ref(props.config.default_frequency === 'monthly' ? 'monthly' : 'once');
const monthly = computed(() => canMonthly.value && frequency.value === 'monthly');
watch(canMonthly, (ok) => { if (!ok) frequency.value = 'once'; });
watch(() => props.config.default_frequency, (f) => { frequency.value = f === 'monthly' ? 'monthly' : 'once'; });

const offeredMethods = computed(() => (monthly.value ? monthlyMethods.value : methods.value));
const methodKey = ref('');
const method = computed(() => offeredMethods.value.find((m) => m.key === methodKey.value) || offeredMethods.value[0] || null);

// ── Amount ──────────────────────────────────────────────────────────
const tiles = computed(() => (Array.isArray(props.config.amounts) ? props.config.amounts : [])
    .map((a) => ({ amount: Number(a?.amount) || 0, note: a?.note || '' }))
    .filter((a) => a.amount > 0));
const picked = ref(1);
const other = ref('');
const allowOther = computed(() => props.config.allow_other !== false);

const otherValue = computed(() => {
    const raw = String(other.value ?? '').replace(/[$,\s]/g, '');
    if (!allowOther.value || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
});
const otherError = computed(() => {
    const v = otherValue.value;
    if (v === null) return '';
    if (Number.isNaN(v) || v <= 0) return 'Enter an amount greater than zero.';
    if (v > MAX_GIFT) return `Online gifts are limited to ${money(MAX_GIFT)}. Contact us for a larger gift.`;
    return '';
});
const usingOther = computed(() => otherValue.value !== null);
const selectedIndex = computed(() => {
    if (usingOther.value || !tiles.value.length) return -1;
    return Math.min(Math.max(picked.value, 0), tiles.value.length - 1);
});
const amount = computed(() => {
    if (usingOther.value) return otherError.value ? null : otherValue.value;
    return selectedIndex.value >= 0 ? tiles.value[selectedIndex.value].amount : null;
});

function pick(i) {
    picked.value = i;
    other.value = '';
}

const ctaLabel = computed(() => {
    if (amount.value === null) return monthly.value ? 'Donate monthly' : 'Donate';
    return `Donate ${money(amount.value)}${monthly.value ? ' / month' : ''}`;
});
const ctaDisabled = computed(() => !!otherError.value);
const href = computed(() => donateUrl({
    amount: amount.value,
    frequency: monthly.value ? 'monthly' : 'once',
    method: method.value?.key,
    purpose: props.config.purpose || '',
}));

function onCta(e) {
    if (editing || ctaDisabled.value) e.preventDefault();
}
</script>

<template>
    <section :id="config.anchor_id || null" class="th-block th-donate th-sec" :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-card th-card--pad th-donate__card">
                <CheckoutNotice kind="donation" />
                <div class="th-stack th-donate__intro">
                    <p v-if="config.eyebrow" class="th-eyebrow th-donate__eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2 th-h2--md">{{ config.heading }}</h2>
                    <p v-if="config.body" class="th-body th-donate__body">{{ config.body }}</p>
                    <div v-if="goal > 0" class="th-donate__progress">
                        <div class="th-donate__figures">
                            <span>{{ money(raised) }} raised</span>
                            <span>Goal {{ money(goal) }}</span>
                        </div>
                        <div class="th-donate__bar" role="progressbar" :aria-valuenow="Math.round(pct)" aria-valuemin="0" aria-valuemax="100">
                            <div class="th-donate__fill" :style="{ width: pct + '%' }"></div>
                        </div>
                        <div v-if="progressMeta" class="th-donate__meta">{{ progressMeta }}</div>
                    </div>
                </div>

                <div class="th-stack th-donate__form">
                    <div v-if="canMonthly" class="th-donate__seg" role="group" aria-label="How often">
                        <button type="button" class="th-donate__seg-btn th-donate-freq-once" :class="{ 'is-active': !monthly }"
                                :aria-pressed="!monthly" @click="frequency = 'once'">One-time</button>
                        <button type="button" class="th-donate__seg-btn th-donate-freq-monthly" :class="{ 'is-active': monthly }"
                                :aria-pressed="monthly" @click="frequency = 'monthly'">Monthly</button>
                    </div>

                    <div v-if="tiles.length" class="th-donate__tiles">
                        <button v-for="(t, i) in tiles" :key="i" type="button"
                                class="th-donate__tile th-donate-amount"
                                :class="{ 'is-active': i === selectedIndex }"
                                :aria-pressed="i === selectedIndex"
                                @click="pick(i)">
                            <span class="th-donate__tile-amount">{{ money(t.amount) }}</span>
                            <span v-if="t.note" class="th-donate__tile-note">{{ t.note }}</span>
                        </button>
                    </div>

                    <div v-if="allowOther">
                        <input v-model="other" type="text" inputmode="decimal"
                               class="th-input th-donate-other" placeholder="Other amount"
                               aria-label="Other amount" :aria-invalid="!!otherError">
                        <div v-if="otherError" class="th-field-error th-donate__error">{{ otherError }}</div>
                    </div>

                    <template v-if="methods.length">
                        <div v-if="offeredMethods.length > 1" class="th-donate__seg th-donate__methods" role="group" aria-label="Pay with"
                             :style="{ gridTemplateColumns: `repeat(${offeredMethods.length}, minmax(0, 1fr))` }">
                            <button v-for="m in offeredMethods" :key="m.key" type="button"
                                    class="th-donate__seg-btn th-donate-method"
                                    :class="{ 'is-active': method && m.key === method.key }"
                                    :aria-pressed="!!method && m.key === method.key"
                                    @click="methodKey = m.key">{{ m.label || m.key }}</button>
                        </div>
                        <a :href="href" class="th-btn th-btn--primary th-btn--block th-donate__cta th-donate-cta"
                           :class="{ 'is-disabled': ctaDisabled }" :aria-disabled="ctaDisabled || null"
                           @click="onCta">{{ ctaLabel }}</a>
                    </template>
                    <p v-else-if="editing" class="th-donate__unset th-donate__unset--editor">
                        Connect PayPal or Stripe in Theme → Donations to take gifts
                    </p>
                    <p v-else class="th-donate__unset">Online giving is not set up yet.</p>

                    <div v-if="config.fine_print" class="th-mono th-donate__fine">{{ config.fine_print }}</div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-donate__card {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
    gap: clamp(24px, 4vw, 56px);
}
.th-donate__intro { --th-gap: 16px; }
.th-donate__eyebrow { margin: 0; }
.th-donate__body { max-width: 44ch; }
.th-donate__progress { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.th-donate__figures {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font: 500 12px/1 var(--th-mono);
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--th-muted);
}
.th-donate__bar { height: 10px; border-radius: 999px; background: var(--tint); overflow: hidden; }
.th-donate__fill { height: 100%; background: var(--accent); border-radius: 999px; }
.th-donate__meta { font-size: 14px; color: var(--th-meta); }
.th-donate__seg {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    padding: 4px;
    background: #f1efe9;
    border-radius: var(--r);
}
.th-donate__seg-btn {
    border: 0;
    background: transparent;
    color: var(--th-ink);
    padding: 10px;
    border-radius: var(--r);
    font-weight: 600;
    font-size: 15px;
}
.th-donate__seg-btn.is-active { background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, .12); }
.th-donate__methods .th-donate__seg-btn { font-size: 14px; padding: 8px; }
.th-donate__tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.th-donate__tile {
    border: 1.5px solid #d0cdc3;
    background: #fff;
    color: var(--th-ink);
    padding: 16px 12px;
    border-radius: var(--r);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
}
.th-donate__tile.is-active {
    border-color: var(--accent);
    background: var(--tint);
}
.th-donate__tile-amount { font: 800 24px/1 var(--th-display); }
.th-donate__tile-note { font-size: 13px; opacity: .85; }
.th-donate__error { margin-top: 6px; }
.th-donate__cta { padding-top: 15px; padding-bottom: 15px; }
.th-donate__cta.is-disabled { opacity: .55; cursor: not-allowed; }
.th-donate__unset {
    margin: 0;
    padding: 14px;
    border-radius: var(--r);
    background: #f1efe9;
    color: var(--th-meta);
    font-size: 15px;
    text-align: center;
}
.th-donate__unset--editor { border: 1px dashed var(--th-field); }
.th-donate__fine { font-size: 11px; text-align: center; text-transform: uppercase; }
</style>
